import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseSmartFilterFormViewModel } from '../base-smart-filter-form-view-model';
import { exists } from '../../../../../functions/exists';

@Injectable()
export class BasicAddEditSmartFilterFormViewModel extends BaseSmartFilterFormViewModel {
  constructor() {
    super();
  }

  private selectedPrimarySortOption = new BehaviorSubject<string>(null);
  public disableSecondarySort$ = combineLatest([this.selectedPrimarySortOption, this.isViewOnly$]).pipe(
    map(([selectedPrimarySort, viewOnly]) => {
      return !selectedPrimarySort || viewOnly;
    })
  );

  private _itemCount = new BehaviorSubject<number>(null);
  public itemCount$ = this._itemCount as Observable<number>;
  public setItemCount = (ic: number) => this._itemCount.next(ic);

  public readonly disableSortDropdowns$ = combineLatest([this.isViewOnly$, this.itemCount$]).pipe(
    map(([viewOnly, itemCount]) => {
      return viewOnly || !itemCount || itemCount < 0;
    })
  );

  public primarySortDropdownOptions$ = window?.types?.groupedSectionSortOptions$;
  public sortDropdownGroupings$ = window?.types?.groupedSectionSortOptionGroupings$;

  public secondarySortDropdownOptions$ = combineLatest([
    this.primarySortDropdownOptions$,
    this.selectedPrimarySortOption
  ]).pipe(
    map(([allDropdowns, primarySort]) => {
      if (!primarySort) {
        return null;
      }
      // Need to remove primarySortOption's ASC & DESC values
      // Take everything before the trailing _ASC / _DESC, e.g. 'TAG_GROUPS_ASC' -> 'TAG_GROUPS'
      const lastUnderscore = primarySort.lastIndexOf('_');
      if (lastUnderscore === -1) {
        return allDropdowns;
      }
      const primaryKey = primarySort.substring(0, lastUnderscore);
      const ascCounterpart = `${primaryKey}_ASC`;
      const descCounterpart = `${primaryKey}_DESC`;

      return allDropdowns.filter(dropdown => {
        return dropdown.value !== ascCounterpart && dropdown.value !== descCounterpart;
      });
    })
  );

  public readonly primarySortTooltip$ = combineLatest([this.isViewOnly$, this.itemCount$]).pipe(
    map(([viewOnly, ic]) => {
      if (!ic && !viewOnly) {
        return 'Item Count required for sorting';
      }
      return (
        'The items pulled in from the smart filter will be sorted using this criteria before the' +
        ' item count is applied'
      );
    })
  );
  public readonly secondarySortTooltip$ = combineLatest([this.isViewOnly$, this.itemCount$]).pipe(
    map(([viewOnly, ic]) => {
      if (!ic && !viewOnly) {
        return 'Item Count required for sorting';
      }
      return (
        'This sorting option will be applied to any items that are in the same position based on ' + 'the primary sort'
      );
    })
  );

  // For UsePurposeType Picker (All, Medicinal, Recreational)
  // Leaving these two pipes in here in case we ever want to add the picker back
  public displayAllRecMedControl$ = of(false);
  public readonly usePurposeOptions$ = of(null);

  public hasSystemIdentifier$ = this.smartFilter$.pipe(map(smartFilter => exists(smartFilter?.systemIdentifier)));

  public primarySortDropdownChanged(sortOption: string) {
    this.selectedPrimarySortOption.next(sortOption);
  }
}
